import { Button, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import ReminderModel, { ReminderView, ReminderData, defaultReminderData } from "../../core/api/reminder/ReminderModel";
import ReminderModalForm from "./ReminderModalForm";
import { translations } from "../../translations/common";

const ViewContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  maxHeight: '500px',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  overflow: 'auto',
}));

const Title = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  fontWeight: 500,
}));

const ReminderText = styled(Typography)(({ theme }) => ({
  flex: 1,
  overflowX: "hidden",
  overflowY: "auto",
  width: "100%",
  marginBottom: theme.spacing(2),
  whiteSpace: 'pre-wrap',
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: 'auto',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: '120px',
}));

export interface Props {
  clientId: string;
}

const ReminderViewBox: React.FC<Props> = ({ clientId }) => {
  const [reminder, setReminder] = useState<ReminderView | null>(null);
  const [edit, setEdit] = useState(false);

  const deleteReminder = () => {
    if (reminder) {
      ReminderModel.delete(reminder.clientId)
        .then(r => setReminder(null));
    }
  };

  const loadReminder = useCallback(() => {
    if (clientId) {
      ReminderModel.reminder(clientId)
        .then((r) => setReminder(r))
        .catch((err) => {
          // Handle error silently
        });
    }
  }, [clientId]);

  useEffect(() => {
    loadReminder();
  }, [loadReminder]);

  const editReminder = () => {
    setEdit(true);
  };

  const closeEdit = () => {
    setEdit(false);
    loadReminder();
  };

  if (!reminder) {
    return (
      <ViewContainer>
        <Title variant="h6">
          Priminimai
        </Title>
        <Typography variant="body1" align="center" sx={{ my: 4 }}>
          Nėra priminimų
        </Typography>
        <ButtonContainer>
          <ActionButton
            variant="contained"
            color="primary"
            onClick={() => setEdit(true)}
          >
            Sukurti
          </ActionButton>
        </ButtonContainer>
        {edit && (
          <ReminderModalForm
            value={defaultReminderData}
            save={(value: ReminderData) => ReminderModel.updateOrCreate(clientId, value)}
            close={closeEdit}
          />
        )}
      </ViewContainer>
    );
  }

  return (
    <ViewContainer>
      <Title variant="h6">
        Priminimai
      </Title>
      <ReminderText>
        {reminder.comment}
      </ReminderText>
      <ButtonContainer>
        <ActionButton
          variant="contained"
          color="primary"
          onClick={editReminder}
        >
          {translations.reminders.table.actions.edit}
        </ActionButton>
        <ActionButton
          variant="contained"
          color="secondary"
          onClick={deleteReminder}
        >
          {translations.reminders.table.actions.delete}
        </ActionButton>
      </ButtonContainer>
      {edit && (
        <ReminderModalForm
          value={{
            date: reminder.date,
            comment: reminder.comment
          }}
          save={(value: ReminderData) => ReminderModel.updateOrCreate(reminder.clientId, value)}
          close={closeEdit}
        />
      )}
    </ViewContainer>
  );
};

export default ReminderViewBox;
